import { parseInput, replaceOrderPlannedTrialsInputSchema, type SetOrderParameterInput } from "@repo/api/schemas";
import {
  displayAnsweredValue,
  replaceOrderPlannedTrials,
  setOrderParameter,
  undeclaredAnswers,
} from "@repo/data-store";
import { asMemberId, asOrderId, asOrderSetId, mintEntityId } from "@repo/domain";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/components/ui/tabs";
import { cn } from "@repo/ui/lib/utils";
import { createContext, type ReactNode, use, useState } from "react";
import { toast } from "sonner";
import { useBookedOrderSetProposal } from "@/features/cases/hooks/use-booked-order-set-proposal";
import { addProcedureToOrderSet } from "@/features/cases/lib/add-procedure-to-order-set";
import { moveProcedureInOrderSet } from "@/features/cases/lib/move-procedure-in-order-set";
import { removeFromOrderSet } from "@/features/cases/lib/remove-from-order-set";
import { splitProcedureInOrderSet } from "@/features/cases/lib/split-procedure-in-order-set";
import { OrderPlannedTrials, type ProtocolBuilderPlannedTrial } from "@/features/catalog";
import type { PresentedOrderSetDto, PresentedOrderSetParameterDto } from "@/lib/order-set-presentation";
import { useAppForm } from "@/shared/forms/app-form";
import { orderParameterAnswer, orderParameterProblem } from "@/shared/forms/order-parameter-answer";
import { qualifiedProcedureTitle } from "@/shared/lib/procedure-title";
import { OrderSetTimelineEditor, OrderSetTimelineProvider } from "@/shared/order-set-timeline/order-set-timeline";
import { StepProcedures } from "@/shared/order-set-timeline/step-procedures";
import { ConcurrentLabel, type EditableTimelineProviderProps } from "@/shared/timeline";
import type { OrderSetEditor } from "../hooks/use-order-set-draft";
import { OrderPickerDialog, type ProcedureOrderOption } from "./order-picker-dialog";
import { OrderSetBookingFeedback } from "./order-set-booking-feedback";
import { OrderSetDraftFeedback } from "./order-set-draft-feedback";

interface ProtocolEditingProviderProps {
  orderSet: PresentedOrderSetDto;
  draft: OrderSetEditor;
  timingEditable: boolean;
  bookedVisitId?: string;
  /**
   * The catalog procedures this surface may order. Omit it and the timeline has
   * no add control at all, which is the right reading for a surface that was
   * never handed a catalog to choose from.
   */
  procedureOptions?: readonly ProcedureOrderOption[];
  children: ReactNode;
}

interface ProtocolEditingValue {
  persisted: PresentedOrderSetDto;
  /** What is being edited: the draft unbooked, the booking proposal booked. */
  orderSet: PresentedOrderSetDto;
  draft: OrderSetEditor;
  bookedVisitId?: string;
  editable: boolean;
  remove?: (orderId: string) => void;
  split: (orderId: string) => void;
  replan: (orderId: string) => ((trials: ProtocolBuilderPlannedTrial[]) => void) | undefined;
  commitBar: ReactNode;
}

const ProtocolEditingContext = createContext<ProtocolEditingValue | null>(null);

export function useProtocolEditing(): ProtocolEditingValue {
  const value = use(ProtocolEditingContext);
  if (!value) throw new Error("useProtocolEditing must be used within a ProtocolEditingProvider");
  return value;
}

/**
 * Edits the patient-specific OrderSet. The protocol definition supplies
 * labels/declarations only; duration and answers are read from and written to
 * the aggregate that Apply later releases.
 *
 * Owns the draft (or booking proposal) and its timeline, so every surface under
 * it — the configure panel, the plan canvas's protocol card — edits the same one.
 */
export function ProtocolEditingProvider({
  orderSet: persisted,
  timingEditable,
  bookedVisitId,
  procedureOptions,
  draft,
  children,
}: ProtocolEditingProviderProps) {
  const proposal = useBookedOrderSetProposal(persisted, bookedVisitId);
  const orderSet = bookedVisitId ? proposal.displayed : draft.displayed;
  const editable = (timingEditable || Boolean(bookedVisitId)) && !proposal.submitting && !draft.saving;
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  // `null` is a real target — "a step of its own" — so closed is its own value rather than null.
  const [picking, setPicking] = useState<{ stepId: string | null } | null>(null);
  const canAdd = Boolean(procedureOptions?.length) && editable;
  const remove = editable
    ? (id: string) => {
        try {
          if (bookedVisitId) proposal.change(removeFromOrderSet(orderSet, id, proposal.persisted));
          else draft.remove(id);
        } catch (cause) {
          toast.error(cause instanceof Error ? cause.message : "Couldn't remove order.");
        }
      }
    : undefined;
  const commit: EditableTimelineProviderProps["onStepsChange"] = ({ steps: next, gesture }) => {
    if (!editable) return;
    const stepIndex = orderSet.steps.findIndex((step) => step.stepId === gesture.stepId);
    const before = orderSet.steps[stepIndex];
    const after = next.find((step) => step.id === gesture.stepId);
    if (!before || !after) {
      toast.error(
        "Couldn't save step placement: the changed step could not be identified. Please reload and try again.",
      );
      return;
    }
    if (bookedVisitId) {
      proposal.change({
        ...orderSet,
        steps: next.flatMap((step) => {
          const original = orderSet.steps.find((candidate) => candidate.stepId === step.id);
          return original ? [{ ...original, placement: step.placement }] : [];
        }),
      });
      return;
    }
    if (gesture.kind === "reorder") {
      draft.change({ kind: "reorderStep", stepIndex, toStep: gesture.toIndex });
    } else if (gesture.kind === "resize") {
      if (after.placement.durationMinutes !== before.placement.durationMinutes)
        draft.change({ kind: "resizeStep", stepIndex, durationMinutes: after.placement.durationMinutes });
    } else if (gesture.kind === "move") {
      if (after.placement.offsetMinutes !== before.placement.offsetMinutes)
        draft.change({ kind: "moveStep", stepIndex, offsetMinutes: after.placement.offsetMinutes });
    } else {
      // A new gesture kind is a compile error here rather than a silent move.
      const unreachable: never = gesture.kind;
      throw new Error(`Unhandled timeline gesture: ${String(unreachable)}`);
    }
  };
  /**
   * The picked definition becomes a draft order on the set. Unbooked, that is one
   * staged `addOrder` intent, which grows the step to contain it and re-settles
   * what follows in the local domain replay. Booked, the same work goes into the booking proposal
   * instead: new timed work moves the reservation, so it is checked and
   * explicitly submitted rather than written on the spot.
   */
  const addProcedure = (stepId: string | null, option: ProcedureOrderOption) => {
    if (!canAdd) return;
    if (bookedVisitId) {
      proposal.change(addProcedureToOrderSet(orderSet, stepId, option, mintEntityId()));
      return;
    }
    const index = stepId === null ? undefined : orderSet.steps.findIndex((step) => step.stepId === stepId);
    if (index !== undefined && index < 0) {
      toast.error(
        "Couldn't add the procedure: the selected step could not be identified. Please reload and try again.",
      );
      return;
    }
    draft.change({
      kind: "addOrder",
      orderId: asOrderId(mintEntityId()),
      definitionUrl: option.definitionUrl,
      expectedVersion: option.version,
      // Answered afterwards, in the order's own card: the catalog declares the
      // parameters, and an order with none of them answered is a valid draft.
      parameterValues: [],
      // Omitted rather than null — that is what `addOrder` reads as "a step of its own at the end".
      ...(index === undefined ? {} : { concurrentWithStep: index }),
    });
  };
  const pickingStepIndex = picking?.stepId
    ? orderSet.steps.findIndex((step) => step.stepId === picking.stepId)
    : undefined;
  /**
   * Unbooked, a re-plan is staged in the draft with the timeline's edits and saved
   * with them. Booked, the draft is not in play, so it is written through the
   * planned-trials intent — the visit's own order sheet does the same — and a
   * proposed order the server has not yet seen cannot be planned until submitted.
   */
  const replan = (orderId: string): ((trials: ProtocolBuilderPlannedTrial[]) => void) | undefined => {
    if (!editable) return undefined;
    if (bookedVisitId && !persisted.orders.some((order) => order.orderId === orderId)) return undefined;
    return (trials) => {
      let input: ReturnType<typeof replaceOrderPlannedTrialsInputSchema.parse>;
      try {
        input = parseInput(replaceOrderPlannedTrialsInputSchema, {
          orderSetId: orderSet.orderSetId,
          orderId,
          plannedTrials: trials,
        });
      } catch {
        toast.error("Couldn't change planned trials.");
        return;
      }
      if (!bookedVisitId) {
        draft.plannedTrials(orderId, input.plannedTrials);
        return;
      }
      replaceOrderPlannedTrials(input).isPersisted.promise.catch(() => {});
    };
  };
  const split = (orderId: string) => {
    if (!editable) return;
    if (bookedVisitId) proposal.change(splitProcedureInOrderSet(orderSet, orderId));
    else draft.change({ kind: "splitOrder", orderId: asOrderId(orderId) });
  };
  const commitBar = bookedVisitId ? (
    <OrderSetBookingFeedback proposal={proposal} orderSet={orderSet} />
  ) : (
    <OrderSetDraftFeedback draft={draft} />
  );

  return (
    <ProtocolEditingContext
      value={{ persisted, orderSet, draft, bookedVisitId, editable, remove, split, replan, commitBar }}
    >
      <OrderSetTimelineProvider
        orderSet={orderSet}
        density="editor"
        editable={editable}
        selectedStepId={selectedStepId}
        onSelectionChange={setSelectedStepId}
        onStepsChange={commit}
        onEnvelopeChange={(minutes) => {
          if (!editable) return;
          if (bookedVisitId) proposal.change({ ...orderSet, envelopeMinutes: minutes });
          else draft.change({ kind: "setEnvelope", envelopeMinutes: minutes });
        }}
        onMoveProcedure={(orderId, stepId) => {
          if (!editable) return;
          const target = orderSet.steps.findIndex((step) => step.stepId === stepId);
          const source = orderSet.steps.find((step) => step.orders.some((order) => order.orderId === orderId));
          const order = source?.orders.find((order) => order.orderId === orderId);
          if (
            !order ||
            target < 0 ||
            source?.stepId === stepId ||
            order.status === "completed" ||
            order.status === "revoked"
          )
            return;
          if (bookedVisitId) proposal.change(moveProcedureInOrderSet(orderSet, orderId, stepId));
          else draft.change({ kind: "moveOrder", orderId: asOrderId(orderId), toStep: target });
          if (selectedStepId === source?.stepId) setSelectedStepId(stepId);
        }}
        onAddProcedure={canAdd ? (stepId) => setPicking({ stepId }) : undefined}
        proposedStepIds={
          bookedVisitId
            ? new Set(orderSet.steps.map((step) => step.stepId).filter((stepId) => !proposal.persisted.has(stepId)))
            : undefined
        }
      >
        {children}
      </OrderSetTimelineProvider>
      <OrderPickerDialog
        open={picking !== null}
        onOpenChange={(open) => !open && setPicking(null)}
        procedures={procedureOptions ?? []}
        target={
          pickingStepIndex !== undefined && pickingStepIndex >= 0
            ? `A procedure runs alongside step ${pickingStepIndex + 1}`
            : "A procedure is added as a step of its own, at the end"
        }
        onSelectProcedure={(option) => {
          if (picking) addProcedure(picking.stepId, option);
          setPicking(null);
        }}
      />
    </ProtocolEditingContext>
  );
}

interface ConfigureProtocolPanelProps extends Omit<ProtocolEditingProviderProps, "children"> {
  /** Off where the visit already exposes parameter configuration in its own sheet. */
  showOrderCards?: boolean;
}

/** The configure panel on its own editing state — for surfaces with nothing else drawing the protocol. */
export function ConfigureProtocolPanel({ showOrderCards, ...props }: ConfigureProtocolPanelProps) {
  return (
    <ProtocolEditingProvider {...props}>
      <ProtocolConfiguration showOrderCards={showOrderCards} />
    </ProtocolEditingProvider>
  );
}

/** Timeline and trials tabs over the nearest `ProtocolEditingProvider`. */
export function ProtocolConfiguration({ showOrderCards = true }: { showOrderCards?: boolean }) {
  const { orderSet, draft, bookedVisitId, editable, remove, split, replan, commitBar } = useProtocolEditing();
  const steps = orderSet.steps
    .map((step) => ({
      stepId: step.stepId,
      durationMinutes: step.placement.durationMinutes,
      orders: step.orders.filter((order) => order.status !== "revoked"),
    }))
    .filter((step) => step.orders.length > 0);

  return (
    <Tabs defaultValue="timeline" className="w-full min-h-0 min-w-0 flex-1 gap-0 overflow-hidden">
      {showOrderCards && (
        <div className="shrink-0 border-b pl-4 pr-12 pt-2">
          <TabsList variant="line" aria-label="Protocol configuration">
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="trials">Trials</TabsTrigger>
          </TabsList>
        </div>
      )}
      <TabsContent value="timeline" keepMounted className="min-h-0 overflow-auto scrollbar-none data-[hidden]:hidden">
        <OrderSetTimelineEditor
          fillHeight={showOrderCards}
          // A booked set has no reset of its own: its way back is Discard, in the
          // commit bar. Two controls calling `proposal.discard()` was one too many.
          onReset={bookedVisitId ? undefined : draft.resetTiming}
          commit={showOrderCards ? undefined : commitBar}
          renderStepProcedures={(stepId) => (
            <StepProcedures
              steps={orderSet.steps}
              stepId={stepId}
              editable={editable}
              onRemove={remove}
              onSplit={split}
            />
          )}
        />
      </TabsContent>
      {showOrderCards && (
        <TabsContent value="trials" className="min-h-0 space-y-4 overflow-auto scrollbar-none p-4">
          {steps.length === 0 && (
            <p className="text-sm text-muted-foreground">This protocol has no procedures to configure.</p>
          )}

          {steps.map(({ stepId, durationMinutes, orders: stepOrders }, index) => (
            <section key={stepId} className={cn("space-y-2", stepOrders.length > 1 && "rounded-lg bg-muted/40 p-2.5")}>
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground tabular-nums">
                  Step {index + 1} · {durationMinutes} min
                </p>
                {stepOrders.length > 1 && <ConcurrentLabel />}
              </div>
              <div className="space-y-3">
                {stepOrders.map((order) => (
                  <OrderConfigurationCard
                    key={order.orderId}
                    orderSetId={orderSet.orderSetId}
                    onParameterChange={draft.parameter}
                    order={order}
                    name={qualifiedProcedureTitle(order.definition.title, order.qualifier)}
                    parameters={order.definition.parameters}
                    plannedTrialsEditable={editable}
                    onReplacePlannedTrials={replan(order.orderId)}
                  />
                ))}
              </div>
            </section>
          ))}
        </TabsContent>
      )}
      {showOrderCards && <div className="shrink-0 border-t bg-muted/30 px-4 py-2">{commitBar}</div>}
    </Tabs>
  );
}

type CuratedOrder = PresentedOrderSetDto["steps"][number]["orders"][number];
type DefinitionParameter = PresentedOrderSetParameterDto;

function OrderConfigurationCard({
  orderSetId,
  onParameterChange,
  order,
  name,
  parameters,
  plannedTrialsEditable,
  onReplacePlannedTrials,
}: {
  orderSetId: string;
  order: CuratedOrder;
  name: string;
  onParameterChange: (input: SetOrderParameterInput) => void;
  parameters: readonly DefinitionParameter[];
  plannedTrialsEditable: boolean;
  onReplacePlannedTrials?: (trials: ProtocolBuilderPlannedTrial[]) => void;
}) {
  // An order answering parameters itself predates planned trials; it keeps its form.
  const answersItself = order.parameterValues.length > 0 && !order.plannedTrials?.length;
  return (
    <div className="overflow-hidden rounded border bg-background">
      <div className="border-b bg-muted px-3 py-2 text-sm font-semibold">
        <span className="truncate">{name}</span>
      </div>
      {answersItself ? (
        <div className="space-y-3 p-3">
          <ParameterForm
            orderSetId={orderSetId}
            order={order}
            parameters={parameters}
            onParameterChange={onParameterChange}
          />
          <UndeclaredAnswers parameters={parameters} answers={order.parameterValues} />
        </div>
      ) : (
        <div className="space-y-3 p-3">
          {plannedTrialsEditable && !onReplacePlannedTrials && (
            <p className="text-xs text-muted-foreground">Submit the proposed changes before planning this procedure.</p>
          )}
          <OrderPlannedTrials order={order} editable={plannedTrialsEditable} onChange={onReplacePlannedTrials} />
        </div>
      )}
    </div>
  );
}

/**
 * Answers a kind revision orphaned. Read-only: with no declaration there is no
 * value kind to edit against, but the answer is still on the ServiceRequest and
 * must be visible rather than silently absent.
 */
function UndeclaredAnswers({
  parameters,
  answers,
}: {
  parameters: readonly DefinitionParameter[];
  answers: CuratedOrder["parameterValues"];
}) {
  const orphaned = undeclaredAnswers(parameters, answers);
  if (!orphaned.length) return null;
  return (
    <div className="space-y-1">
      {orphaned.map((answer) => (
        <div key={`${answer.source}:${answer.parameterId}`} className="flex justify-between gap-4 text-xs">
          <span className="text-muted-foreground">
            {answer.parameterId}
            <span className="block text-muted-foreground/70">No longer declared</span>
          </span>
          <span className="text-right">{displayAnsweredValue(undefined, answer.value)}</span>
        </div>
      ))}
    </div>
  );
}

type FieldValue = string | readonly string[] | boolean | number | null | undefined;
type Answer = CuratedOrder["parameterValues"][number]["value"];

function fieldName(parameter: DefinitionParameter): string {
  return `${parameter.source}:${parameter.parameterId}`;
}

/**
 * The server cannot accept answers for an order until its staged addition is saved.
 */
function ParameterForm({
  orderSetId,
  order,
  parameters,
  onParameterChange,
}: {
  orderSetId: string;
  order: CuratedOrder;
  parameters: readonly DefinitionParameter[];
  onParameterChange?: (input: SetOrderParameterInput) => void;
}) {
  const stored = new Map<string, Answer>(
    order.parameterValues.map((answer) => [`${answer.source}:${answer.parameterId}`, answer.value]),
  );
  const form = useAppForm({
    defaultValues: Object.fromEntries(
      parameters.map((parameter) => [fieldName(parameter), editableValue(parameter, stored.get(fieldName(parameter)))]),
    ) as Record<string, FieldValue>,
  });

  const save = (parameter: DefinitionParameter, value: FieldValue) => {
    if (orderParameterProblem(parameter, value)) return;
    const answer = orderParameterAnswer(parameter.value, value);
    const input: SetOrderParameterInput = {
      orderSetId: asOrderSetId(orderSetId),
      orderId: asOrderId(order.orderId),
      source: parameter.source === "self" ? "self" : asMemberId(parameter.source),
      parameterId: asMemberId(parameter.parameterId),
      value: answer,
    };
    if (onParameterChange) {
      onParameterChange(input);
      return;
    }
    setOrderParameter(input).isPersisted.promise.catch(() => {});
  };

  return (
    <form.AppForm>
      <div className="space-y-3">
        {parameters.map((parameter) => {
          const definition = parameter.value;
          // Typing is settled on change, except for free-typed text and numbers:
          // saving those per keystroke would write once per character.
          const onEdit = { onChange: ({ value }: { value: FieldValue }) => save(parameter, value) };
          const onCommit = { onBlur: ({ value }: { value: FieldValue }) => save(parameter, value) };
          return (
            <form.AppField
              key={fieldName(parameter)}
              name={fieldName(parameter)}
              validators={{ onChange: ({ value }) => orderParameterProblem(parameter, value) }}
              listeners={definition.kind === "Choice" || definition.kind === "Boolean" ? onEdit : onCommit}
            >
              {(field) => {
                if (definition.kind === "Boolean")
                  return (
                    <field.SwitchField label={parameter.label} description={parameter.sourceLabel} isRequired={false} />
                  );
                if (definition.kind === "Choice") {
                  const options = definition.options.map((option) => ({
                    value: option.optionId,
                    label: option.label,
                  }));
                  return definition.cardinality === "Multiple" ? (
                    <field.MultiOptionGroupField
                      label={parameter.label}
                      description={parameter.sourceLabel}
                      isRequired={false}
                      options={options}
                    />
                  ) : (
                    <field.SelectField
                      label={parameter.label}
                      description={parameter.sourceLabel}
                      isRequired={false}
                      options={options}
                    />
                  );
                }
                if (definition.kind === "Integer" || definition.kind === "Quantity") {
                  return (
                    <field.NumberField
                      declaration={definition}
                      label={parameter.label}
                      description={parameter.sourceLabel}
                      isRequired={false}
                      min={definition.min}
                      max={definition.max}
                      allowOutOfRange
                      step={definition.kind === "Integer" ? 1 : "any"}
                    />
                  );
                }
                return (
                  <field.InputField label={parameter.label} description={parameter.sourceLabel} isRequired={false} />
                );
              }}
            </form.AppField>
          );
        })}
      </div>
    </form.AppForm>
  );
}

/**
 * An unanswered parameter must edit as empty, never as its first option: the
 * fields render a placeholder for a falsy value, which is what makes an
 * unanswered setting read as unanswered.
 */
function editableValue(parameter: DefinitionParameter, answer: Answer | undefined): FieldValue {
  switch (parameter.value.kind) {
    case "Choice": {
      const selected = answer?.kind === "Choice" ? [...answer.selected] : [];
      return parameter.value.cardinality === "Multiple" ? selected : (selected[0] ?? "");
    }
    case "Boolean":
      return answer?.kind === "Boolean" ? answer.value : false;
    case "Text":
      return answer?.kind === "Text" ? answer.value : "";
    default:
      return answer && "value" in answer && typeof answer.value === "number" ? answer.value : undefined;
  }
}
